(function() {
  'use strict';

  // 1. Sticky/Transparent Header with IntersectionObserver
  // Observe the hero section. When it's in view, header is transparent.
  // When scrolled past, header gets solid background.
  function initHeader() {
    const header = document.getElementById('site-header');
    if (!header) return;

    const isTransparent = header.dataset.transparent === 'true';
    const isSticky = header.dataset.sticky === 'true';

    if (!isSticky && !isTransparent) return;

    // Find a hero that opts in via [data-hero]. Pages without one (e.g. PDP)
    // fall through to the solid header so its text/logo stays visible.
    const hero = document.querySelector('[data-hero]');

    if (isTransparent && hero) {
      header.classList.add('header--transparent');

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            header.classList.add('header--transparent');
            header.classList.remove('header--solid');
          } else {
            header.classList.remove('header--transparent');
            header.classList.add('header--solid');
          }
        },
        { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
      );
      observer.observe(hero);
    } else {
      header.classList.add('header--solid');
      // No hero behind the fixed header, so reserve its height to keep the
      // page's first content (e.g. the PDP breadcrumb) from hiding underneath.
      const main = document.getElementById('main-content');
      if (main) main.style.paddingTop = header.offsetHeight + 'px';
    }
  }

  // 2. Mobile Menu Drawer
  function initMobileMenu() {
    const toggleBtns = document.querySelectorAll('[data-mobile-menu-toggle]');
    const menu = document.getElementById('mobile-menu');
    if (!menu) return;

    const panel = menu.querySelector('[data-mobile-menu-panel]');
    const overlay = menu.querySelector('[data-mobile-menu-overlay]');
    const closeBtns = menu.querySelectorAll('[data-mobile-menu-close]');

    function openMenu() {
      menu.classList.remove('invisible', 'opacity-0');
      menu.classList.add('visible', 'opacity-100');
      panel.classList.remove('-translate-y-full');
      panel.classList.add('translate-y-0');
      document.body.style.overflow = 'hidden';
      // Focus trap - focus the first close button
      closeBtns[0] && closeBtns[0].focus();
    }

    function closeMenu() {
      menu.classList.add('invisible', 'opacity-0');
      menu.classList.remove('visible', 'opacity-100');
      panel.classList.add('-translate-y-full');
      panel.classList.remove('translate-y-0');
      document.body.style.overflow = '';
      // Return focus to the toggle button that opened the menu
      if (toggleBtns[0]) toggleBtns[0].focus();
    }

    // Focus trap: keep Tab cycling within the open menu
    function trapFocus(e) {
      if (e.key !== 'Tab' || menu.classList.contains('invisible')) return;
      const focusable = panel.querySelectorAll(
        'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    toggleBtns.forEach(btn => btn.addEventListener('click', openMenu));
    closeBtns.forEach(btn => btn.addEventListener('click', closeMenu));
    overlay && overlay.addEventListener('click', closeMenu);

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !menu.classList.contains('invisible')) {
        closeMenu();
      }
    });
    document.addEventListener('keydown', trapFocus);
  }

  // 3. Shop Dropdown Toggle
  function initDropdowns() {
    document.querySelectorAll('[data-dropdown]').forEach(dropdown => {
      const toggle = dropdown.querySelector('[data-dropdown-toggle]');
      const menu = dropdown.querySelector('[data-dropdown-menu]');
      if (!toggle || !menu) return;

      function setOpen(open) {
        if (open) {
          menu.classList.remove('opacity-0', 'invisible');
          menu.classList.add('opacity-100', 'visible');
        } else {
          menu.classList.add('opacity-0', 'invisible');
          menu.classList.remove('opacity-100', 'visible');
        }
        toggle.setAttribute('aria-expanded', String(open));
      }

      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = !menu.classList.contains('invisible');
        setOpen(!isOpen);
      });

      // Close on outside click
      document.addEventListener('click', () => setOpen(false));
    });
  }

  // 3b. Header Search Panel (predictive search)
  // Opens a drop-down search over the header instead of navigating to /search.
  // Live results come from Shopify's /search/suggest.json; Enter still submits
  // the <form> to the full search results page.
  function initSearch() {
    const wrap = document.querySelector('[data-search]');
    if (!wrap) return;

    const toggle = wrap.querySelector('[data-search-toggle]');
    const field = wrap.querySelector('[data-search-field]');         // desktop inline field
    const mobilebar = wrap.querySelector('[data-search-mobilebar]'); // mobile full-width field bar
    const panel = wrap.querySelector('[data-search-panel]');         // results dropdown
    const closeBtns = wrap.querySelectorAll('[data-search-close]');
    const inputs = wrap.querySelectorAll('[data-search-input]');     // desktop + mobile inputs
    const defaultList = wrap.querySelector('[data-search-default]');
    const liveList = wrap.querySelector('[data-search-live]');
    if (!toggle || !panel) return;

    const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;

    function showDefault() {
      if (defaultList) defaultList.classList.remove('hidden');
      if (liveList) liveList.innerHTML = '';
    }

    function isOpen() {
      return !panel.classList.contains('invisible');
    }

    function open() {
      toggle.classList.add('opacity-0');                 // hide the icon, keep its space (no reflow)
      if (field) field.classList.add('lg:flex');         // desktop inline field; stays hidden on mobile
      if (mobilebar) { mobilebar.classList.remove('hidden'); mobilebar.classList.add('flex'); } // mobile bar
      panel.classList.remove('invisible', 'opacity-0');
      panel.classList.add('visible', 'opacity-100');
      showDefault();
      const focusTarget = isDesktop()
        ? wrap.querySelector('[data-search-field] [data-search-input]')
        : (mobilebar && mobilebar.querySelector('[data-search-input]'));
      focusTarget && focusTarget.focus();
    }

    function close() {
      toggle.classList.remove('opacity-0');
      if (field) field.classList.remove('lg:flex');
      if (mobilebar) { mobilebar.classList.add('hidden'); mobilebar.classList.remove('flex'); }
      panel.classList.add('invisible', 'opacity-0');
      panel.classList.remove('visible', 'opacity-100');
    }

    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      isOpen() ? close() : open();
    });
    closeBtns.forEach(btn => btn.addEventListener('click', (e) => {
      e.stopPropagation();
      close();
    }));

    // Close on outside click (desktop dropdown) and on Escape.
    document.addEventListener('click', (e) => {
      if (!isOpen()) return;
      if (wrap.contains(e.target)) return;
      close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen()) close();
    });

    if (!inputs.length || !liveList) return;

    function escapeHtml(s) {
      return s.replace(/[&<>"']/g, c => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    }

    // Bold the matched portion of the title, mirroring the brand two-tone style.
    function highlight(title, q) {
      const idx = title.toLowerCase().indexOf(q.toLowerCase());
      if (idx === -1) return escapeHtml(title);
      return escapeHtml(title.slice(0, idx)) +
        '<strong>' + escapeHtml(title.slice(idx, idx + q.length)) + '</strong>' +
        escapeHtml(title.slice(idx + q.length));
    }

    function render(products, q) {
      if (defaultList) defaultList.classList.add('hidden'); // hide popular searches while typing
      liveList.innerHTML = products.length
        ? products.map(p =>
            '<a href="' + p.url + '" class="search-result">' + highlight(p.title, q) + '</a>'
          ).join('')
        : '<p class="search-empty">No results found.</p>';
    }

    let timer;
    let lastQuery = '';

    function fetchResults(q) {
      const url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
      fetch(url)
        .then(r => r.json())
        .then(data => {
          if (q !== lastQuery) return; // a newer keystroke superseded this request
          const products = (data.resources && data.resources.results && data.resources.results.products) || [];
          render(products, q);
        })
        .catch(() => {});
    }

    inputs.forEach(input => {
      input.addEventListener('input', () => {
        const q = input.value.trim();
        lastQuery = q;
        inputs.forEach(other => { if (other !== input) other.value = input.value; }); // keep both inputs in sync
        clearTimeout(timer);
        if (q.length < 2) {
          showDefault();
          return;
        }
        timer = setTimeout(() => fetchResults(q), 200);
      });
    });
  }

  // 4. Collection Slider Arrows
  function initSliders() {
    document.querySelectorAll('[data-slider]').forEach(slider => {
      const track = slider.querySelector('[data-slider-track]');
      const prevBtn = slider.querySelector('[data-slider-prev]');
      const nextBtn = slider.querySelector('[data-slider-next]');
      if (!track) return;

      // Advance by one item's width when the track exposes [data-slider-item];
      // otherwise fall back to the legacy fixed amount.
      function step() {
        const item = track.querySelector('[data-slider-item]');
        if (item) {
          const styles = getComputedStyle(track);
          const gap = parseFloat(styles.columnGap || styles.gap) || 0;
          return item.getBoundingClientRect().width + gap;
        }
        return 320;
      }

      prevBtn && prevBtn.addEventListener('click', () => {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      nextBtn && nextBtn.addEventListener('click', () => {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
    });
  }

  // 5. Category Tab Switching
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach(tabContainer => {
      const buttons = tabContainer.querySelectorAll('[data-tab-button]');
      const panels = tabContainer.querySelectorAll('[data-tab-panel]');

      // Set up ARIA roles
      const tablist = buttons[0] && buttons[0].parentElement;
      if (tablist) tablist.setAttribute('role', 'tablist');

      buttons.forEach((button, i) => {
        button.setAttribute('role', 'tab');
        const panelId = 'tabpanel-' + button.dataset.tabButton;
        button.setAttribute('aria-controls', panelId);
        button.setAttribute('aria-selected', button.classList.contains('tab--active') ? 'true' : 'false');
      });

      panels.forEach(p => {
        p.setAttribute('role', 'tabpanel');
        p.id = 'tabpanel-' + p.dataset.tabPanel;
      });

      function activate(target) {
        buttons.forEach(b => {
          const active = b.dataset.tabButton === target;
          b.classList.toggle('tab--active', active);
          b.setAttribute('aria-selected', String(active));
        });

        panels.forEach(p => {
          if (p.dataset.tabPanel === target) {
            p.classList.remove('hidden');
          } else {
            p.classList.add('hidden');
          }
        });
      }

      buttons.forEach(button => {
        button.addEventListener('click', () => activate(button.dataset.tabButton));
      });
    });
  }

  // 6. Product card image carousel (prev/next arrows + pagination dots)
  function initProductCardCarousels() {
    document.querySelectorAll('[data-product-media]').forEach(root => {
      if (root.dataset.carouselInit) return; // idempotent: skip already-initialized cards
      root.dataset.carouselInit = '1';
      const track = root.querySelector('[data-product-media-track]');
      const slides = root.querySelectorAll('[data-product-media-slide]');
      const dots = root.querySelectorAll('[data-product-media-dot]');
      const prevBtn = root.querySelector('[data-product-media-prev]');
      const nextBtn = root.querySelector('[data-product-media-next]');
      if (!track || slides.length <= 1) return;

      const count = slides.length;

      // Clone last + first slide onto each end so the loop never rewinds:
      // advancing past the end slides forward onto a clone, then snaps invisibly.
      const cloneLast = slides[count - 1].cloneNode(true);
      const cloneFirst = slides[0].cloneNode(true);
      [cloneLast, cloneFirst].forEach((c) => {
        c.setAttribute('aria-hidden', 'true');
        c.removeAttribute('data-product-media-slide');
      });
      track.insertBefore(cloneLast, slides[0]);
      track.appendChild(cloneFirst);

      let index = 0;   // logical slide 0..count-1 (for the dots)
      let pos = 1;     // physical position; the real slides occupy 1..count
      let animating = false;

      function place(animate) {
        track.style.transition = animate ? '' : 'none';
        track.style.transform = 'translateX(-' + pos * 100 + '%)';
        if (!animate) void track.offsetWidth; // commit instant jumps
      }

      function updateDots() {
        dots.forEach((dot, i) => {
          dot.classList.toggle('opacity-100', i === index);
          dot.classList.toggle('opacity-30', i !== index);
        });
      }

      function step(dir) {
        if (animating) return;
        animating = true;
        pos += dir;
        index = (index + dir + count) % count;
        place(true);
        updateDots();
      }

      function goTo(i) {
        if (animating || i === index) return;
        animating = true;
        index = i;
        pos = i + 1;
        place(true);
        updateDots();
      }

      track.addEventListener('transitionend', (e) => {
        if (e.propertyName !== 'transform') return;
        animating = false;
        if (pos === 0) { pos = count; place(false); }            // landed on cloned-last → real last
        else if (pos === count + 1) { pos = 1; place(false); }   // landed on cloned-first → real first
      });

      prevBtn && prevBtn.addEventListener('click', (e) => { e.preventDefault(); step(-1); });
      nextBtn && nextBtn.addEventListener('click', (e) => { e.preventDefault(); step(1); });
      dots.forEach((dot, i) => {
        dot.addEventListener('click', (e) => { e.preventDefault(); goTo(i); });
      });

      // Drag / swipe (pointer events unify mouse + touch)
      let startX = 0;
      let dragging = false;
      let moved = false;
      let width = 1;

      track.addEventListener('pointerdown', (e) => {
        if (animating) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        width = root.clientWidth || 1;
        track.style.transition = 'none';
        track.setPointerCapture(e.pointerId);
      });

      track.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const delta = e.clientX - startX;
        if (Math.abs(delta) > 5) moved = true;
        track.style.transform = 'translateX(calc(-' + pos * 100 + '% + ' + delta + 'px))';
      });

      function endDrag(e) {
        if (!dragging) return;
        dragging = false;
        const delta = e.clientX - startX;
        const threshold = width * 0.15;
        if (delta < -threshold) step(1);
        else if (delta > threshold) step(-1);
        else place(true); // snap back
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);

      // Swallow the click that follows a drag so slide links don't navigate
      track.addEventListener('click', (e) => { if (moved) e.preventDefault(); }, true);

      // Autoplay / loop (opt-in via data-product-media-autoplay="<ms>")
      const autoplayMs = parseInt(root.getAttribute('data-product-media-autoplay'), 10);
      if (autoplayMs > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        let timer = null;
        const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
        const start = () => { stop(); timer = setInterval(() => step(1), autoplayMs); };
        root.addEventListener('pointerenter', stop);
        root.addEventListener('pointerleave', start);
        root.addEventListener('pointerdown', stop);
        start();
      }

      place(false);
      updateDots();
    });
  }

  // 7. Wishlist — heart toggle with localStorage persistence
  var WISHLIST_KEY = 'mauve:wishlist';

  function wishlistGet() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; }
    catch (e) { return []; }
  }
  function wishlistSet(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) {}
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list: list } }));
  }
  function wishlistHas(handle) { return wishlistGet().indexOf(handle) !== -1; }
  function wishlistToggle(handle) {
    var list = wishlistGet();
    var i = list.indexOf(handle);
    if (i === -1) list.push(handle); else list.splice(i, 1);
    wishlistSet(list);
    return i === -1; // true if now added
  }

  // Reflect a toggle button's pressed/filled state.
  function paintHeart(btn, active) {
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    var svg = btn.querySelector('[data-heart]');
    var path = svg && svg.querySelector('path');
    if (path) path.setAttribute('d', active ? svg.dataset.pathFilled : svg.dataset.pathOutline);
  }

  function wireWishlistButton(btn) {
    if (btn.dataset.wlInit) return; // idempotent
    btn.dataset.wlInit = '1';
    var handle = btn.getAttribute('data-wishlist-handle');
    if (handle) paintHeart(btn, wishlistHas(handle)); // initial state from storage

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (handle) {
        paintHeart(btn, wishlistToggle(handle));
      } else {
        // No handle (decorative) — just flip the icon visually.
        paintHeart(btn, btn.getAttribute('aria-pressed') !== 'true');
      }
    });
  }

  // Header wishlist icon badge — mirrors the cart count badge.
  function updateWishlistCount() {
    var n = wishlistGet().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('hidden', n === 0);
    });
  }

  var wishlistSyncBound = false;
  function initWishlistToggles() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(wireWishlistButton);
    updateWishlistCount(); // initial badge from storage

    // Keep all hearts for the same product in sync across the page (bind once).
    if (!wishlistSyncBound) {
      wishlistSyncBound = true;
      document.addEventListener('wishlist:change', function () {
        document.querySelectorAll('[data-wishlist-toggle][data-wishlist-handle]').forEach(function (btn) {
          paintHeart(btn, wishlistHas(btn.getAttribute('data-wishlist-handle')));
        });
        updateWishlistCount();
      });
    }
  }

  // 7a. Wishlist page — render saved products into the grid.
  function initWishlistPage() {
    var root = document.querySelector('[data-wishlist-page]');
    if (!root) return;
    var grid = root.querySelector('[data-wishlist-grid]');
    var empty = root.querySelector('[data-wishlist-empty]');
    if (!grid) return;

    var rendering = false;
    function render() {
      if (rendering) return;
      var handles = wishlistGet();
      if (!handles.length) {
        grid.innerHTML = '';
        if (empty) empty.classList.remove('hidden');
        return;
      }
      if (empty) empty.classList.add('hidden');
      rendering = true;

      // Fetch each product's card markup (alternate "card" template) in order.
      Promise.all(handles.map(function (handle) {
        return fetch('/products/' + handle + '?view=card')
          .then(function (r) { return r.ok ? r.text() : ''; })
          .catch(function () { return ''; });
      })).then(function (htmls) {
        grid.innerHTML = '';
        htmls.forEach(function (html) {
          if (!html) return;
          var tmp = document.createElement('div');
          tmp.innerHTML = html.trim();
          var card = tmp.querySelector('[data-product-card]') || tmp.firstElementChild;
          if (card) grid.appendChild(card);
        });
        // Wire interactive bits on the freshly injected cards.
        initProductCardCarousels();
        initWishlistToggles();
        rendering = false;
      });
    }

    render();
    // Re-render when an item is removed (re-render only when count changes to avoid loops).
    var lastCount = wishlistGet().length;
    document.addEventListener('wishlist:change', function () {
      var n = wishlistGet().length;
      if (n !== lastCount) { lastCount = n; render(); }
    });
  }

  // 7b. Standalone PDP quantity stepper (display only — no cart calls).
  // Shows zero-padded values ("01", "02", …).
  function initPdpQty() {
    document.querySelectorAll('[data-pdp-qty]').forEach(stepper => {
      if (stepper._bound) return;
      stepper._bound = true;
      const input = stepper.querySelector('[data-qty-input]');
      if (!input) return;
      const read = () => Math.max(1, parseInt(input.value, 10) || 1);
      const write = (q) => { input.value = String(q).padStart(2, '0'); };
      const minus = stepper.querySelector('[data-qty-minus]');
      const plus = stepper.querySelector('[data-qty-plus]');
      minus && minus.addEventListener('click', () => write(Math.max(1, read() - 1)));
      plus && plus.addEventListener('click', () => write(read() + 1));
      input.addEventListener('change', () => write(read()));
    });
  }

  // 7c. Generic modals — [data-modal-open="name"] opens [data-modal="name"].
  // Animation is CSS-driven via the .is-open class (fade overlay + rise panel).
  function initModals() {
    const openModal = (modal) => {
      // Only one modal at a time (e.g. switching Sign In <-> Create Account)
      document.querySelectorAll('[data-modal].is-open').forEach(m => {
        if (m !== modal) m.classList.remove('is-open');
      });
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      const closeBtn = modal.querySelector('[data-modal-close]');
      closeBtn && closeBtn.focus();
    };
    const closeModal = (modal) => {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    document.querySelectorAll('[data-modal-open]').forEach(btn => {
      if (btn._modalBound) return;
      btn._modalBound = true;
      btn.addEventListener('click', async () => {
        const name = btn.getAttribute('data-modal-open');

        // On mobile, prefer the device's native share sheet over the share modal.
        if (name === 'share' && navigator.share && window.matchMedia('(max-width: 767px)').matches) {
          const title = btn.getAttribute('data-share-title') || document.title;
          try {
            await navigator.share({ title, url: location.href });
            return;
          } catch (e) {
            if (e && e.name === 'AbortError') return; // user dismissed the sheet
            // otherwise fall through to the custom modal
          }
        }

        const modal = document.querySelector('[data-modal="' + name + '"]');
        modal && openModal(modal);
      });
    });

    document.querySelectorAll('[data-modal]').forEach(modal => {
      if (modal._modalBound) return;
      modal._modalBound = true;
      modal.querySelectorAll('[data-modal-close], [data-modal-overlay]').forEach(el => {
        el.addEventListener('click', () => closeModal(modal));
      });
      // Re-open after a server round-trip (contact form success / errors)
      if (modal.querySelector('[data-modal-autoopen]')) openModal(modal);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('[data-modal].is-open').forEach(closeModal);
    });

    // Share modal actions: copy current URL / share via WhatsApp
    document.querySelectorAll('[data-share-copy]').forEach(btn => {
      btn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(location.href);
          const note = btn.closest('[data-modal]') && btn.closest('[data-modal]').querySelector('[data-share-copied-note]');
          if (note) {
            note.classList.remove('hidden');
            setTimeout(() => note.classList.add('hidden'), 2000);
          }
        } catch (e) { /* clipboard unavailable */ }
      });
    });
    document.querySelectorAll('[data-share-whatsapp]').forEach(link => {
      const title = link.getAttribute('data-share-title') || document.title;
      link.href = 'https://wa.me/?text=' + encodeURIComponent(title + ' ' + location.href);
    });

    // Password "Show"/"Hide" toggles (account modals)
    document.querySelectorAll('[data-password-toggle]').forEach(btn => {
      if (btn._bound) return;
      btn._bound = true;
      btn.addEventListener('click', () => {
        const input = btn.closest('div') && btn.closest('div').querySelector('input');
        if (!input) return;
        const show = input.type === 'password';
        input.type = show ? 'text' : 'password';
        btn.setAttribute('aria-pressed', show ? 'true' : 'false');
        const label = btn.querySelector('[data-password-toggle-label]');
        if (label) label.textContent = show ? 'Hide' : 'Show';
      });
    });
  }

  // 7d. AJAX submit for every MOMA Forms app form (contact-us, enquiry,
  // customization). Any form carrying [data-form-ajax] is intercepted here,
  // POSTed to the app proxy with _mode=json, and answered with the shared
  // success/failure popup (snippets/form-result-modal.liquid) — no full-page
  // round-trip, so the older ?form_posted= redirect path is only a no-JS
  // fallback now.
  //
  // Per-form copy overrides (all optional) are read off the form element:
  //   data-success-title / data-success-body / data-error-title / data-error-body
  // Bodies are "~"-delimited to allow two lines, matching the snippet.
  function initAjaxForms() {
    const forms = document.querySelectorAll('form[data-form-ajax]');
    if (!forms.length) return;

    const resultModal = document.querySelector('[data-modal="form-result"]');

    // Swap the popup into the given state, applying this form's copy overrides
    // and restoring the snippet's defaults when a form doesn't override them.
    const showResult = (state, form) => {
      if (!resultModal) return;
      resultModal.setAttribute('data-result', state);

      const apply = (container, attr, asLines) => {
        if (!container) return;
        const slot = container.querySelector('[data-result-view="' + state + '"]');
        if (!slot) return;
        const custom = form.getAttribute(attr);
        if (custom) {
          // Remember the snippet default once so a later form can restore it.
          if (slot.hasAttribute('data-result-default') && slot._defaultHTML == null) {
            slot._defaultHTML = slot.innerHTML;
          }
          slot.innerHTML = asLines
            ? custom.split('~').map((l) => '<p>' + escapeHtml(l.trim()) + '</p>').join('')
            : escapeHtml(custom);
        } else if (slot._defaultHTML != null) {
          slot.innerHTML = slot._defaultHTML;
        }
      };

      apply(resultModal.querySelector('[data-result-title]'), 'data-' + state + '-title', false);
      apply(resultModal.querySelector('[data-result-body]'), 'data-' + state + '-body', true);

      // Close whatever modal the form lives in, then open the result popup.
      document.querySelectorAll('[data-modal].is-open').forEach((m) => {
        if (m !== resultModal) m.classList.remove('is-open');
      });
      resultModal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      const closeBtn = resultModal.querySelector('[data-modal-close]');
      closeBtn && closeBtn.focus();
    };

    forms.forEach((form) => {
      if (form._ajaxBound) return;
      form._ajaxBound = true;

      // Inline fallback message, used only when the popup isn't on the page.
      const errorEl = form.querySelector('[data-form-error]');
      const submitBtn = form.querySelector('[type="submit"]');

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        // Let the browser surface required/type validation first.
        if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;
        if (form._submitting) return;
        form._submitting = true;

        if (errorEl) errorEl.classList.add('hidden');
        const originalLabel = submitBtn ? submitBtn.textContent : '';
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending…'; }

        try {
          const body = new FormData(form);
          // Force JSON mode even if the markup omits the hidden _mode input.
          body.set('_mode', 'json');

          const res = await fetch(form.action, {
            method: 'POST',
            body,
            headers: { 'Accept': 'application/json' }
          });
          let data = null;
          try { data = await res.json(); } catch (_) { /* non-JSON */ }

          if (res.ok && data && data.ok) {
            form.reset();
            showResult('success', form);
          } else {
            throw new Error((data && data.error) || 'form-submit-failed');
          }
        } catch (err) {
          if (resultModal) {
            showResult('error', form);
          } else if (errorEl) {
            errorEl.textContent = 'Something went wrong. Please try again.';
            errorEl.classList.remove('hidden');
          }
        } finally {
          form._submitting = false;
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalLabel; }
        }
      });
    });
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  // 7b. Full-page form success/error handling for the MOMA Forms app.
  // The app proxy 302-redirects back with ?form_posted=<key> (or ?form_error=1).
  // Reveal the matching form's [data-form-success]/[data-form-error], reopen its
  // parent modal if it lives in one, then strip the params from the URL.
  function initFormSuccess() {
    const params = new URLSearchParams(window.location.search);
    const posted = params.get('form_posted');
    const errored = params.get('form_error');
    if (!posted && !errored) return;

    const key = posted || errored;
    // Prefer the form whose data-form-app matches the posted key; otherwise the
    // first app form on the page.
    const form =
      document.querySelector('[data-form-app="' + key + '"]') ||
      document.querySelector('[data-form-app]');

    if (form) {
      const target = form.querySelector(posted ? '[data-form-success]' : '[data-form-error]');
      if (target) target.classList.remove('hidden');

      // If the form sits inside a modal, reopen it and scroll the message in.
      const modal = form.closest('[data-modal]');
      if (modal) {
        document.querySelectorAll('[data-modal].is-open').forEach((m) => {
          if (m !== modal) m.classList.remove('is-open');
        });
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }
      if (target && target.scrollIntoView) target.scrollIntoView({ block: 'center' });
    }

    // Clean the URL so a refresh doesn't re-show the banner.
    params.delete('form_posted');
    params.delete('form_error');
    const qs = params.toString();
    const url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    window.history.replaceState({}, '', url);
  }

  // 8. Mega-menu furniture hover -> show product image inside the watermark
  function initMegaHover() {
    const target = document.querySelector('[data-mega-hover-image]');
    if (!target) return;

    // Hide gracefully if an image fails to load (e.g. asset not added yet)
    target.addEventListener('error', () => {
      target.classList.add('opacity-0');
      target.classList.remove('opacity-100');
    });

    document.querySelectorAll('[data-hover-image]').forEach((item) => {
      const src = item.getAttribute('data-hover-image');
      if (!src) return;
      item.addEventListener('mouseenter', () => {
        target.src = src;
        target.classList.remove('opacity-0');
        target.classList.add('opacity-100');
      });
      item.addEventListener('mouseleave', () => {
        target.classList.add('opacity-0');
        target.classList.remove('opacity-100');
      });
    });
  }

  // Mega-menu accordions (native <details>) — only one open at a time per group.
  function initMenuAccordions() {
    document.querySelectorAll('[data-accordion-group]').forEach((group) => {
      const items = group.querySelectorAll('details[data-accordion]');
      items.forEach((d) => {
        d.addEventListener('toggle', () => {
          if (!d.open) return;
          items.forEach((other) => { if (other !== d) other.open = false; });
        });
      });
    });
  }

  // 9. Product detail gallery: thumbnails + prev/next arrows + finish-swatch sync + share
  function initProductGallery() {
    document.querySelectorAll('[data-pdp-gallery]').forEach(gallery => {
      const slides = Array.from(gallery.querySelectorAll('[data-pdp-slide]'));
      const thumbs = Array.from(gallery.querySelectorAll('[data-pdp-thumb]'));
      const prev = gallery.querySelector('[data-pdp-prev]');
      const next = gallery.querySelector('[data-pdp-next]');
      if (!slides.length) return;

      let index = 0;
      function show(i) {
        index = (i + slides.length) % slides.length;
        slides.forEach((s, n) => s.classList.toggle('hidden', n !== index));
        thumbs.forEach((t, n) => t.classList.toggle('is-active', n === index));
      }

      prev && prev.addEventListener('click', () => show(index - 1));
      next && next.addEventListener('click', () => show(index + 1));
      thumbs.forEach((t, n) => t.addEventListener('click', () => show(n)));

      // Let the PDP variant form jump the gallery (e.g. to the variant image slide).
      gallery._show = show;

      // Finish swatches: set active state + optionally jump the gallery to a media index
      const finishes = Array.from(gallery.closest('.container')?.querySelectorAll('[data-pdp-finish]') || []);
      finishes.forEach(btn => {
        btn.addEventListener('click', () => {
          finishes.forEach(b => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
          const target = btn.getAttribute('data-pdp-finish-index');
          if (target !== null && slides[+target]) show(+target);
        });
      });

      // Fullscreen lightbox: click a gallery image to open; navigate + download.
      const lightbox = gallery.querySelector('[data-pdp-lightbox]');
      if (lightbox) {
        // Portal to <body> so the fixed overlay sits in the root stacking context
        // and covers the sticky header instead of being trapped under it.
        if (lightbox.parentElement !== document.body) document.body.appendChild(lightbox);
        const lbImg = lightbox.querySelector('[data-lightbox-img]');
        const lbClose = lightbox.querySelector('[data-lightbox-close]');
        const lbPrev = lightbox.querySelector('[data-lightbox-prev]');
        const lbNext = lightbox.querySelector('[data-lightbox-next]');
        const lbDownload = lightbox.querySelector('[data-lightbox-download]');

        // One entry per gallery slide image (hi-res source + filename for download).
        const items = slides.map(s => {
          const img = s.querySelector('img');
          if (!img) return null;
          return {
            full: img.getAttribute('data-full') || img.currentSrc || img.src,
            alt: img.alt || '',
            name: img.getAttribute('data-filename') || 'image'
          };
        }).filter(Boolean);

        let lbIndex = 0;
        function lbShow(i) {
          if (!items.length) return;
          lbIndex = (i + items.length) % items.length;
          const it = items[lbIndex];
          lbImg.src = it.full;
          lbImg.alt = it.alt;
          if (lbDownload) { lbDownload.setAttribute('href', it.full); lbDownload.dataset.name = it.name; }
        }
        function lbOpen(i) { lbShow(i); lightbox.classList.remove('hidden'); document.body.style.overflow = 'hidden'; }
        function lbCloseFn() { lightbox.classList.add('hidden'); document.body.style.overflow = ''; }

        slides.forEach((s, n) => {
          const img = s.querySelector('[data-pdp-zoom]');
          img && img.addEventListener('click', () => lbOpen(n));
        });

        lbClose && lbClose.addEventListener('click', lbCloseFn);
        lbPrev && lbPrev.addEventListener('click', () => lbShow(lbIndex - 1));
        lbNext && lbNext.addEventListener('click', () => lbShow(lbIndex + 1));

        // Click on the dark backdrop (outside image/controls) closes.
        lightbox.addEventListener('click', (e) => {
          if (!e.target.closest('[data-lightbox-img], [data-lightbox-download], [data-lightbox-prev], [data-lightbox-next], [data-lightbox-close]')) {
            lbCloseFn();
          }
        });

        document.addEventListener('keydown', (e) => {
          if (lightbox.classList.contains('hidden')) return;
          if (e.key === 'Escape') lbCloseFn();
          else if (e.key === 'ArrowLeft') lbShow(lbIndex - 1);
          else if (e.key === 'ArrowRight') lbShow(lbIndex + 1);
        });

        // Download: fetch as a blob so the CDN (cross-origin) image saves with a
        // filename instead of just navigating. Falls back to opening in a tab.
        lbDownload && lbDownload.addEventListener('click', async (e) => {
          e.preventDefault();
          const url = lbDownload.getAttribute('href');
          if (!url) return;
          try {
            const res = await fetch(url, { mode: 'cors' });
            const blob = await res.blob();
            const ext = ((blob.type.split('/')[1]) || 'jpg').replace('jpeg', 'jpg').split('+')[0];
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objUrl;
            a.download = (lbDownload.dataset.name || 'image') + '.' + ext;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
          } catch (err) {
            window.open(url, '_blank');
          }
        });
      }

      show(0);
    });

    // Share button: native share with clipboard fallback
    document.querySelectorAll('[data-pdp-share]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const url = btn.getAttribute('data-share-url') || location.href;
        const title = btn.getAttribute('data-share-title') || document.title;
        if (navigator.share) {
          try { await navigator.share({ title, url }); } catch (e) { /* dismissed */ }
        } else if (navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(url);
            btn.setAttribute('aria-label', 'Link copied');
          } catch (e) { /* no-op */ }
        }
      });
    });
  }

  // ──────────────────────────────────────────────────────────────────────
  // Cart: AJAX add / change / remove, slide-in drawer, and live re-render.
  // Uses Shopify's Cart AJAX API (/cart/add.js, /cart/change.js) plus the
  // Section Rendering API (?section_id=cart-drawer) to refresh the markup.
  // ──────────────────────────────────────────────────────────────────────

  // Shared: re-render the drawer + cart page + header count after a mutation.
  async function refreshCart() {
    // Header count from the cart object
    let cart;
    try {
      cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
    } catch (e) { cart = null; }

    if (cart) {
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.classList.toggle('hidden', cart.item_count === 0);
      });
      document.querySelectorAll('[data-cart-title-count]').forEach(el => {
        const lines = (cart.items || []).length;
        el.textContent = '(' + lines + ' ' + (lines === 1 ? 'Item' : 'Items') + ')';
      });
    }

    // Re-render the drawer contents via the Section Rendering API
    try {
      const html = await fetch('/?section_id=cart-drawer').then(r => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer-contents]');
      const current = document.querySelector('#cart-drawer [data-cart-drawer-contents]');
      if (fresh && current) {
        current.replaceWith(fresh);
        // Bind qty/remove handlers on the freshly rendered drawer rows
        initCart();
      }
    } catch (e) { /* leave existing markup */ }

    // On the full cart page, re-render the section in place via the Section
    // Rendering API so line totals + summary stay server-authoritative without
    // a jarring full reload. Falls back to a reload if anything goes wrong.
    const cartPage = document.querySelector('[data-cart-page]');
    if (cartPage) {
      const section = cartPage.closest('.shopify-section');
      const id = section && section.id.replace('shopify-section-', '');
      try {
        if (!id) throw new Error('no section id');
        const html = await fetch('/cart?section_id=' + encodeURIComponent(id)).then(r => r.text());
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-page]');
        if (!fresh) throw new Error('no cart markup');
        cartPage.replaceWith(fresh);
        initCart();
      } catch (e) {
        window.location.reload();
      }
    }
  }

  // Slide-in drawer open/close (mirrors the mobile-menu pattern).
  function initCartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    const panel = drawer.querySelector('[data-cart-drawer-panel]');
    const overlay = drawer.querySelector('[data-cart-drawer-overlay]');

    function open() {
      drawer.classList.remove('invisible', 'opacity-0');
      drawer.classList.add('visible', 'opacity-100');
      panel.classList.remove('translate-x-full');
      panel.classList.add('translate-x-0');
      document.body.style.overflow = 'hidden';
      const close = drawer.querySelector('[data-cart-drawer-close]');
      close && close.focus();
    }

    function close() {
      drawer.classList.add('invisible', 'opacity-0');
      drawer.classList.remove('visible', 'opacity-100');
      panel.classList.add('translate-x-full');
      panel.classList.remove('translate-x-0');
      document.body.style.overflow = '';
    }

    // Expose for initCart (add-to-cart should open the drawer)
    drawer._open = open;
    drawer._close = close;

    document.querySelectorAll('[data-cart-drawer-toggle]').forEach(btn => btn.addEventListener('click', open));
    overlay && overlay.addEventListener('click', close);

    // Delegated so re-rendered contents (close buttons) keep working
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-drawer-close]')) close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !drawer.classList.contains('invisible')) close();
    });

    // Focus trap within the open drawer
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || drawer.classList.contains('invisible')) return;
      const focusable = panel.querySelectorAll('a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  // Add-to-cart, qty steppers and remove buttons (drawer + cart page).
  function initCart() {
    const openDrawer = () => {
      const drawer = document.getElementById('cart-drawer');
      drawer && drawer._open && drawer._open();
    };

    // Animate the add-to-cart button through its states:
    //   default → 'adding'  (Adding…, dimmed)
    //           → 'added'   (Added to cart ✓)
    //           → reset back to the default label.
    const ADDED_CHECK = ' <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-2px;margin-left:6px"><circle cx="12" cy="12" r="9"/><path d="m8.5 12 2.5 2.5 4.5-5"/></svg>';
    const setAddBtnState = (btn, state) => {
      if (!btn) return;
      const span = btn.querySelector('[data-add-to-cart-text]');
      const label = (attr, fallback) => (span && span.getAttribute(attr)) || fallback;
      btn.classList.remove('opacity-80', 'pointer-events-none');
      if (state === 'adding') {
        btn.classList.add('opacity-80', 'pointer-events-none');
        if (span) span.textContent = label('data-adding', 'Adding…');
      } else if (state === 'added') {
        if (span) span.innerHTML = label('data-added', 'Added to cart') + ADDED_CHECK;
      } else if (span) {
        span.textContent = label('data-available', 'Add to cart');
      }
    };

    // Add to cart from the PDP form and card quick-add forms (AJAX).
    document.querySelectorAll('[data-product-form]').forEach(form => {
      if (form._bound) return;
      form._bound = true;
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[data-add-to-cart]');
        const errEl = form.querySelector('[data-cart-error]');
        const isCard = form.hasAttribute('data-card-form');
        if (errEl) { errEl.classList.add('hidden'); errEl.textContent = ''; }
        if (btn) { btn.disabled = true; setAddBtnState(btn, 'adding'); }
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.description || data.message || 'Could not add to cart');
          }
          await refreshCart();
          if (btn) setAddBtnState(btn, 'added');
          const variantModal = form.closest('[data-variant-modal]');
          if (variantModal) {
            // Quick-add from the "Select variant" modal: confirm, then close it
            // and reveal the cart drawer.
            setTimeout(() => {
              variantModal.classList.add('hidden');
              document.body.style.overflow = '';
              openDrawer();
            }, 650);
          } else if (!isCard) {
            // PDP opens the drawer; inline cards just give button feedback.
            openDrawer();
          }
          if (btn) setTimeout(() => { btn.disabled = false; setAddBtnState(btn, 'reset'); }, 1800);
        } catch (err) {
          if (errEl) { errEl.textContent = err.message; errEl.classList.remove('hidden'); }
          if (btn) { btn.disabled = false; setAddBtnState(btn, 'reset'); }
        }
      });
    });

    // Quantity change via change.js { id (line key), quantity }
    async function changeQty(lineKey, quantity) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity })
        });
        await refreshCart();
      } catch (e) { /* no-op */ }
    }

    // Delegate qty +/- and remove across drawer + cart page (survives re-render)
    document.querySelectorAll('[data-cart-qty]').forEach(stepper => {
      if (stepper._bound) return;
      stepper._bound = true;
      const input = stepper.querySelector('[data-qty-input]');
      const key = stepper.getAttribute('data-line-key');
      const minus = stepper.querySelector('[data-qty-minus]');
      const plus = stepper.querySelector('[data-qty-plus]');
      minus && minus.addEventListener('click', () => {
        const q = Math.max(0, (parseInt(input.value, 10) || 1) - 1);
        changeQty(key, q);
      });
      plus && plus.addEventListener('click', () => {
        const q = (parseInt(input.value, 10) || 1) + 1;
        changeQty(key, q);
      });
      input && input.addEventListener('change', () => {
        const q = Math.max(0, parseInt(input.value, 10) || 0);
        changeQty(key, q);
      });
    });

    document.querySelectorAll('[data-cart-remove]').forEach(btn => {
      if (btn._bound) return;
      btn._bound = true;
      btn.addEventListener('click', () => changeQty(btn.getAttribute('data-line-key'), 0));
    });
  }

  // PDP product form: keep the hidden variant id, price and button in sync as
  // finish swatches / option selects change.
  function initProductForm() {
    document.querySelectorAll('[data-product-form]').forEach(form => {
      if (form.hasAttribute('data-card-form')) return; // cards bind via initCardQuickAdd
      const container = form.closest('.container') || document;
      bindVariantForm(form, {
        swatchSelector: '[data-pdp-finish][data-pdp-option-value]',
        swatchScope: container,
        priceEl: container.querySelector('[data-pdp-price]'),
        compareEl: container.querySelector('[data-pdp-compare]'),
        onSelect: function (variant) {
          // Per-variant dimension lines (rendered by the Dimensions accordion)
          container.querySelectorAll('[data-variant-dims]').forEach(el => {
            el.classList.toggle('hidden', el.getAttribute('data-variant-dims') !== String(variant.id));
          });
          // Variant image slide + thumb: swap to the selected variant's image and show it
          if (variant.image) {
            const slideImg = container.querySelector('[data-pdp-variant-slide] img');
            const thumbImg = container.querySelector('[data-pdp-variant-thumb] img');
            if (slideImg) {
              slideImg.removeAttribute('srcset');
              slideImg.src = variant.image;
              if (variant.image_full) slideImg.setAttribute('data-full', variant.image_full);
            }
            if (thumbImg) thumbImg.src = variant.image;
            const gallery = container.querySelector('[data-pdp-gallery]');
            if (gallery && gallery._show) gallery._show(0);
          }
        }
      });
    });
  }

  // Shared variant binding for the PDP form and card quick-add forms.
  // Reads [data-pdp-variants] JSON, tracks the chosen option value per position
  // (from swatch buttons + option selects), and keeps the hidden id, price, and
  // add-to-cart button in sync. opts.onSelect(variant) fires on every change so
  // callers (cards) can swap the displayed image.
  function bindVariantForm(form, opts) {
    opts = opts || {};
    const dataEl = form.querySelector('[data-pdp-variants]');
    const idInput = form.querySelector('[data-variant-id]');
    if (!dataEl || !idInput) return;

    let variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

    const swatchScope = opts.swatchScope || form;
    const swatchSelector = opts.swatchSelector || '[data-card-swatch]';
    const swatchBtns = Array.from(swatchScope.querySelectorAll(swatchSelector));
    const selects = Array.from(form.querySelectorAll('[data-pdp-option-select]'));
    const priceEl = opts.priceEl || null;
    const compareEl = opts.compareEl || null;
    const addBtn = form.querySelector('[data-add-to-cart]');
    const addBtnText = form.querySelector('[data-add-to-cart-text]');

    const posAttr = opts.positionAttr || 'data-pdp-option-position';
    const valAttr = opts.valueAttr || 'data-pdp-option-value';

    // Track chosen value per option position (init from any pre-pressed swatch
    // in each group + the selects).
    const selected = {};
    swatchBtns.forEach(b => {
      if (b.getAttribute('aria-pressed') === 'true') selected[b.getAttribute(posAttr)] = b.getAttribute(valAttr);
    });
    selects.forEach(sel => { selected[sel.getAttribute('data-pdp-option-position')] = sel.value; });

    function match() {
      return variants.find(v => v.options.every((opt, i) => {
        const pos = String(i + 1);
        return selected[pos] === undefined || selected[pos] === opt;
      }));
    }

    function update() {
      const variant = match();
      if (!variant) return;
      idInput.value = variant.id;
      if (priceEl) priceEl.textContent = variant.price_formatted;
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = variant.compare_at_formatted;
          compareEl.classList.remove('hidden');
        } else {
          compareEl.classList.add('hidden');
        }
      }
      if (addBtn) addBtn.disabled = !variant.available;
      if (addBtnText) {
        const t = addBtnText.getAttribute('data-' + (variant.available ? 'available' : 'soldout'));
        addBtnText.textContent = variant.available
          ? (t || addBtnText.textContent)
          : (addBtnText.getAttribute('data-soldout') || 'Sold Out');
      }
      if (typeof opts.onSelect === 'function') opts.onSelect(variant);
    }

    swatchBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const pos = btn.getAttribute(posAttr);
        // Only reset pressed state within the same option group, so multi-option
        // products keep one selection per group.
        swatchBtns.forEach(b => {
          if (b.getAttribute(posAttr) === pos) b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        selected[pos] = btn.getAttribute(valAttr);
        update();
      });
    });
    selects.forEach(sel => {
      sel.addEventListener('change', () => {
        selected[sel.getAttribute('data-pdp-option-position')] = sel.value;
        update();
      });
    });

    update();
  }

  // Card quick-add: bind each card's variant form + swatches. The card form has
  // [data-product-form][data-card-form]; its submit is handled by initCart().
  // Swatches use [data-card-swatch] with data-option-position / data-option-value
  // and an optional data-media-index used to jump the card's image carousel.
  function initCardQuickAdd() {
    document.querySelectorAll('[data-product-form][data-card-form]').forEach(form => {
      if (form._cardBound) return;
      form._cardBound = true;

      const card = form.closest('[data-product-card]') || form.parentElement;
      const media = card && card.querySelector('[data-product-media]');

      bindVariantForm(form, {
        swatchScope: card || form,
        swatchSelector: '[data-card-swatch]',
        positionAttr: 'data-option-position',
        valueAttr: 'data-option-value',
        onSelect: function () { /* image swap handled on click below for snappiness */ }
      });

      // Image swap: clicking a swatch jumps the card carousel to that variant's media.
      if (media) {
        card.querySelectorAll('[data-card-swatch]').forEach(btn => {
          btn.addEventListener('click', () => {
            const idx = parseInt(btn.getAttribute('data-media-index'), 10);
            if (!isNaN(idx)) jumpCardMedia(media, idx);
          });
        });
      }
    });
  }

  // "Select variant" modal: opened by a card's slide-up "Choose options" button
  // ([data-variant-modal-open]). Variant resolution + add-to-cart are handled by
  // initCardQuickAdd / initCart; this only wires open/close.
  function initVariantModals() {
    // The modal ships inside an inert <template> per card and is cloned into the
    // card on the first "Choose options" click. After cloning, re-running the
    // idempotent initCart/initCardQuickAdd binds the new form into the shared
    // AJAX-cart + variant plumbing (already-bound nodes are skipped via guards).
    const wire = (modal) => {
      if (modal._bound) return;
      modal._bound = true;
      const close = () => { modal.classList.add('hidden'); document.body.style.overflow = ''; };
      const overlay = modal.querySelector('[data-variant-modal-overlay]');
      overlay && overlay.addEventListener('click', close);
      modal.querySelectorAll('[data-variant-modal-close]').forEach(btn => btn.addEventListener('click', close));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modal.classList.contains('hidden')) close();
      });
    };

    document.querySelectorAll('[data-variant-modal-open]').forEach(opener => {
      if (opener._modalBound) return;
      opener._modalBound = true;
      const card = opener.closest('[data-product-card]') || opener.parentElement;

      opener.addEventListener('click', () => {
        if (!card) return;
        let modal = card.querySelector('[data-variant-modal]');
        if (!modal) {
          const tpl = card.querySelector('template[data-variant-modal-template]');
          if (!tpl) return;
          card.appendChild(tpl.content.cloneNode(true));
          modal = card.querySelector('[data-variant-modal]');
          if (!modal) return;
          initCart();
          initCardQuickAdd();
        }
        wire(modal);
        modal.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
      });
    });
  }

  // Jump a [data-product-media] carousel to a given (0-based) logical slide.
  // The carousel clones first/last slides, so the physical position is index+1.
  function jumpCardMedia(media, index) {
    const track = media.querySelector('[data-product-media-track]');
    const slides = media.querySelectorAll('[data-product-media-slide]');
    const dots = media.querySelectorAll('[data-product-media-dot]');
    if (!track || index < 0 || index >= slides.length) return;
    if (track.children.length > slides.length) {
      // cloned ends present → physical position is index + 1
      track.style.transition = '';
      track.style.transform = 'translateX(-' + (index + 1) * 100 + '%)';
    } else {
      track.style.transform = 'translateX(-' + index * 100 + '%)';
    }
    dots.forEach((dot, i) => {
      dot.classList.toggle('opacity-100', i === index);
      dot.classList.toggle('opacity-30', i !== index);
    });
  }

  // Collection toolbar: sort dropdown navigation + mobile filter drawer.
  function initCollectionToolbar() {
    // Sort: navigate to the chosen ?sort_by= value
    document.querySelectorAll('[data-collection-sort]').forEach(sel => {
      sel.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sel.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });

    // Mobile filter drawer (reuses the slide-in pattern)
    const drawer = document.querySelector('[data-filter-drawer]');
    if (drawer) {
      const panel = drawer.querySelector('[data-filter-drawer-panel]');
      const overlay = drawer.querySelector('[data-filter-drawer-overlay]');
      const open = () => {
        drawer.classList.remove('invisible', 'opacity-0');
        drawer.classList.add('visible', 'opacity-100');
        panel && panel.classList.remove('translate-x-full');
        panel && panel.classList.add('translate-x-0');
        document.body.style.overflow = 'hidden';
      };
      const close = () => {
        drawer.classList.add('invisible', 'opacity-0');
        drawer.classList.remove('visible', 'opacity-100');
        panel && panel.classList.add('translate-x-full');
        panel && panel.classList.remove('translate-x-0');
        document.body.style.overflow = '';
      };
      document.querySelectorAll('[data-filter-drawer-toggle]').forEach(b => b.addEventListener('click', open));
      drawer.querySelectorAll('[data-filter-drawer-close]').forEach(b => b.addEventListener('click', close));
      overlay && overlay.addEventListener('click', close);
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && !drawer.classList.contains('invisible')) close();
      });
    }

    // Auto-submit the filter form when a filter input changes (desktop sidebar)
    document.querySelectorAll('[data-filter-form]').forEach(form => {
      form.querySelectorAll('input[type="checkbox"]').forEach(input => {
        input.addEventListener('change', () => form.submit());
      });
    });
  }

  // Pincode availability: check input against a theme-setting list (exact or prefix).
  function initPincodeCheck() {
    document.querySelectorAll('[data-pincode-form]').forEach(form => {
      const input = form.querySelector('[data-pincode-input]');
      const btn = form.querySelector('[data-pincode-check]');
      const result = form.querySelector('[data-pincode-result]');
      if (!input || !result) return;

      const list = (form.getAttribute('data-serviceable') || '')
        .split(/[\s,]+/).map(s => s.trim()).filter(Boolean);

      function show(msg, ok) {
        result.textContent = msg;
        result.classList.remove('hidden', 'text-green-600', 'text-red-600');
        result.classList.add(ok ? 'text-green-600' : 'text-red-600');
      }

      function check() {
        const val = (input.value || '').trim();
        if (!/^\d{3,}$/.test(val)) {
          show(result.getAttribute('data-invalid'), false);
          return;
        }
        const ok = list.some(p => val === p || val.startsWith(p));
        const tmpl = result.getAttribute(ok ? 'data-success' : 'data-fail') || '';
        show(tmpl.replace('%pincode%', val), ok);
      }

      btn && btn.addEventListener('click', check);
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); check(); } });
    });
  }

  // Customer addresses: toggle the add/edit forms and confirm before delete.
  function initCustomerAddresses() {
    const root = document.querySelector('[data-customer-addresses]');
    if (!root) return;

    root.querySelectorAll('[data-address-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const target = document.getElementById(btn.getAttribute('data-address-toggle'));
        if (target) target.classList.toggle('hidden');
      });
    });

    // Country -> province linkage using Shopify's helper if present
    if (window.Shopify && window.Shopify.CountryProvinceSelector) {
      root.querySelectorAll('[data-address-country]').forEach(country => {
        const province = root.querySelector('#' + country.getAttribute('data-province-id'));
        const wrapper = province && province.closest('[data-province-wrapper]');
        if (!province) return;
        new window.Shopify.CountryProvinceSelector(country.id, province.id, {
          hideElement: wrapper ? wrapper.id : undefined
        });
      });
    }
  }

  // Auto cross-fading slider (no arrows / dots, not user-controllable).
  // A [data-fab-slider] holds one or more [data-fab-track] lists; the Nth
  // [data-fab-slide] of every track is shown together, advancing on an interval
  // (data-interval, ms). One slide → stays static. Honors reduced-motion.
  function initFadeSliders() {
    document.querySelectorAll('[data-fab-slider]').forEach(root => {
      if (root._fabInit) return;
      root._fabInit = true;

      const tracks = [...root.querySelectorAll('[data-fab-track]')]
        .map(t => [...t.querySelectorAll('[data-fab-slide]')])
        .filter(slides => slides.length);
      if (!tracks.length) return;

      const count = Math.max(...tracks.map(s => s.length));
      if (count <= 1) return; // single slide: leave the .is-active one showing
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const show = (n) => tracks.forEach(slides => {
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === n % slides.length));
      });

      let index = 0;
      const interval = parseInt(root.getAttribute('data-interval'), 10) || 4000;
      show(0);
      setInterval(() => { index = (index + 1) % count; show(index); }, interval);
    });
  }

  // Searchable country-code dropdown. Enhances [data-country-select]: builds a
  // filterable list from the hidden native <select> (the form value + a11y
  // fallback), with a search box and a scrollable, height-capped panel.
  function initCountryDropdown() {
    document.querySelectorAll('[data-country-select]').forEach(root => {
      if (root._cdInit) return;
      root._cdInit = true;

      const native = root.querySelector('[data-country-native]');
      const trigger = root.querySelector('[data-country-trigger]');
      const label = root.querySelector('[data-country-label]');
      const caret = root.querySelector('[data-country-caret]');
      const panel = root.querySelector('[data-country-panel]');
      const search = root.querySelector('[data-country-search]');
      const list = root.querySelector('[data-country-list]');
      const empty = root.querySelector('[data-country-empty]');
      if (!native || !trigger || !panel || !list) return;

      function setLabel(text) {
        const parts = (text || '').trim().split(/\s+/);
        label.textContent = ((parts[0] || '') + ' ' + (parts[1] || '')).trim();
      }

      function makeItem(opt) {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('role', 'option');
        b.setAttribute('data-cd-option', '');
        b.dataset.value = opt.value;
        b.dataset.search = (opt.textContent || '').toLowerCase();
        b.className = 'w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[var(--color-foreground)]/5 transition-colors';
        b.textContent = opt.textContent.trim();
        if (opt.selected) b.setAttribute('aria-selected', 'true');
        b.addEventListener('click', () => choose(opt.value, opt.textContent.trim(), b));
        li.appendChild(b);
        return li;
      }

      // Build list, preserving optgroup labels as headers
      Array.from(native.children).forEach(node => {
        if (node.tagName === 'OPTGROUP') {
          const h = document.createElement('li');
          h.setAttribute('data-cd-header', '');
          h.className = 'px-3 pt-3 pb-1 text-[11px] uppercase tracking-[0.15em] text-[var(--color-foreground)]/45';
          h.textContent = node.label;
          list.appendChild(h);
          Array.from(node.children).forEach(opt => list.appendChild(makeItem(opt)));
        } else if (node.tagName === 'OPTION') {
          list.appendChild(makeItem(node));
        }
      });

      const sel = native.options[native.selectedIndex];
      if (sel) setLabel(sel.textContent);

      function choose(value, text, btn) {
        native.value = value;
        native.dispatchEvent(new Event('change', { bubbles: true }));
        setLabel(text);
        list.querySelectorAll('[data-cd-option][aria-selected]').forEach(b => b.removeAttribute('aria-selected'));
        if (btn) btn.setAttribute('aria-selected', 'true');
        close();
        trigger.focus();
      }

      function filter(q) {
        q = q.trim().toLowerCase();
        let any = false;
        const children = Array.from(list.children);
        children.forEach(li => {
          const b = li.querySelector('[data-cd-option]');
          if (b) {
            const match = !q || b.dataset.search.indexOf(q) !== -1;
            li.classList.toggle('hidden', !match);
            if (match) any = true;
          }
        });
        // Headers visible only when they have a visible option beneath them
        let headerLi = null, headerHasVisible = false;
        children.forEach(li => {
          if (li.hasAttribute('data-cd-header')) {
            if (headerLi) headerLi.classList.toggle('hidden', !headerHasVisible);
            headerLi = li; headerHasVisible = false;
          } else if (!li.classList.contains('hidden')) {
            headerHasVisible = true;
          }
        });
        if (headerLi) headerLi.classList.toggle('hidden', !headerHasVisible);
        empty && empty.classList.toggle('hidden', any);
      }

      function open() {
        panel.classList.remove('hidden');
        trigger.setAttribute('aria-expanded', 'true');
        caret && caret.classList.add('rotate-180');
        if (search) { search.value = ''; }
        filter('');
        list.scrollTop = 0;
        setTimeout(() => search && search.focus(), 0);
      }
      function close() {
        panel.classList.add('hidden');
        trigger.setAttribute('aria-expanded', 'false');
        caret && caret.classList.remove('rotate-180');
      }

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        panel.classList.contains('hidden') ? open() : close();
      });
      search && search.addEventListener('input', () => filter(search.value));
      search && search.addEventListener('keydown', (e) => { if (e.key === 'Escape') { close(); trigger.focus(); } });
      document.addEventListener('click', (e) => { if (!root.contains(e.target)) close(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.classList.contains('hidden')) close(); });
    });
  }

  // Initialize all on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initMobileMenu();
    initDropdowns();
    initSearch();
    initSliders();
    initTabs();
    initFadeSliders();
    initProductCardCarousels();
    initWishlistToggles();
    initWishlistPage();
    initPdpQty();
    initModals();
    initAjaxForms();
    initFormSuccess();
    initCountryDropdown();
    initMegaHover();
    initMenuAccordions();
    initProductGallery();
    initCartDrawer();
    initCart();
    initProductForm();
    initCardQuickAdd();
    initVariantModals();
    initCollectionToolbar();
    initPincodeCheck();
    initCustomerAddresses();
  });
})();
